// Savidna CRM - Type Definitions

export type UserRole =
  | 'administrador'
  | 'doctor'
  | 'recepcionista'
  | 'callcenter'

export type AppointmentStatus =
  | 'programada'
  | 'completada'
  | 'cancelada'
  | 'reprogramada'

export type OpportunityStage =
  | 'contacto_inicial'
  | 'evaluacion'
  | 'procedimiento_programado'
  | 'completado'
  | 'facturacion'

export type LeadStatus =
  | 'nuevo'
  | 'contactado'
  | 'calificado'
  | 'cita_programada'
  | 'convertido'
  | 'perdido'

export type LeadSource =
  | 'web'
  | 'telefono'
  | 'referido'
  | 'redes_sociales'
  | 'evento'
  | 'otro'

export type Gender = 'masculino' | 'femenino' | 'otro'

export type PermissionLevel = 'full' | 'read' | 'own' | 'none'

// NEW: Role and Permission interfaces for database
export interface Role {
  id: string
  name: string
  displayName: string
  description: string
  isSystemRole: boolean
  createdAt: Date
  updatedAt: Date
}

export interface Permission {
  id: string
  roleId: string
  module: string
  canCreate: boolean
  canRead: boolean
  canUpdate: boolean
  canDelete: boolean
  permissionLevel: PermissionLevel
  createdAt: Date
  updatedAt: Date
}

export interface User {
  id: string
  email: string
  password: string
  name: string
  role: UserRole
  avatar?: string
  language: string
  timezone: string
  createdAt: Date
}

export interface Patient {
  id: string
  name: string
  age: number
  gender: Gender
  phone: string
  email: string
  address: string
  emergencyContact: {
    name: string
    relationship: string
    phone: string
  }
  insuranceId: string | null
  medicalHistory: MedicalHistory
  interactions: Interaction[]
  createdAt: Date
}

export interface MedicalHistory {
  initialInterview: {
    background: string
    visitReason: string
    allergies: string
    currentMedications: string
    familyHistory: string
  }
  symptoms: {
    description: string
    duration: string
    severity: string
    notes: string
  }
  physicalExam: {
    bloodPressure: string
    bloodSugar: string
    heartRate: string
    lungs: string
    eyes: string
    throat: string
    abdomen: string
    weight: string
    height: string
    temperature: string
  }
  instruments: string[]
  diagnosis: string
  recommendations: string
  entries: MedicalEntry[]
}

export interface MedicalEntry {
  id: string
  date: Date
  doctorId: string
  doctorName: string
  appointmentId: string
  notes: string
  diagnosis: string
  treatment: string
  followUp: string
}

export interface ClinicalHistory {
  id: string
  patientId: string
  date: Date
  doctorId: string
  doctorName: string
  personalBackground: {
    fullName: string
    birthDate: string
    address: string
    phone: string
    email: string
    age: number
    gender: string
    occupation: string
    numberOfChildren: number
    deliveryType: string
    alcohol: string
    tobacco: string
    drugs: string
    infusions: string
    consultationReason: string
  }
  clinicalBackground: {
    diseases: string
    currentMedications: string
    chronicDiseases: string
    surgicalHistory: string
    implantsOrDevices: string
    implantType: string
    allergies: string
  }
  diagnosis: string
  treatment: string
  medicalIndications: string
  sessions: string
  sessionNumber: string
  comments: string
  doctorSignature: string
  createdAt: Date
  updatedAt: Date
}

export interface Doctor {
  id: string
  name: string
  specialtyId: string
  phone: string
  email: string
  schedule: DoctorSchedule
  createdAt: Date
}

export interface DoctorSchedule {
  monday: TimeSlot | null
  tuesday: TimeSlot | null
  wednesday: TimeSlot | null
  thursday: TimeSlot | null
  friday: TimeSlot | null
  saturday: TimeSlot | null
  sunday: TimeSlot | null
}

export interface TimeSlot {
  start: string
  end: string
}

export interface Appointment {
  id: string
  patientId: string
  doctorId: string
  serviceId: string
  date: string
  time: string
  duration: number
  status: AppointmentStatus
  notes: string
  nextAppointmentDate?: string
  nextAppointmentNotes?: string
  createdAt: Date
  updatedAt: Date
  cancelReason?: string
  originalDate?: string
  originalTime?: string
}

export interface Service {
  id: string
  name: string
  duration: number
  price: number
  description: string
}

export interface Insurance {
  id: string
  name: string
  company: string
  coverageType: string
  phone: string
  email: string
}

export interface Specialty {
  id: string
  name: string
  description: string
}

export interface Contact {
  id: string
  name: string
  phone: string
  email: string
  company: string
  position: string
  notes: string
  interactions: Interaction[]
  createdAt: Date
}

export interface Company {
  id: string
  name: string
  industry: string
  size: string
  location: string
  phone: string
  email: string
  website?: string
  notes: string
  createdAt: Date
}

export interface Lead {
  id: string
  name: string
  phone: string
  email: string
  source: LeadSource
  status: LeadStatus
  assignedTo: string | null // User ID
  interestedService: string
  notes: string
  interactions: Interaction[]
  createdAt: Date
  updatedAt: Date
}

export interface Interaction {
  id: string
  type: 'llamada' | 'correo' | 'reunion' | 'nota'
  date: Date
  description: string
  userId: string
}

export interface Opportunity {
  id: string
  name: string
  contactId: string | null
  companyId?: string | null
  company: string // Deprecated, kept for backward compatibility
  industry: string
  size: string
  location: string
  stage: OpportunityStage
  amount: number
  probability: number
  notes: string
  interactions: Interaction[]
  createdAt: Date
  updatedAt: Date
}

export interface SystemNotification {
  id: string
  type:
    | 'appointment_created'
    | 'appointment_cancelled'
    | 'appointment_rescheduled'
    | 'appointment_reminder'
    | 'system'
  title: string
  message: string
  read: boolean
  createdAt: Date
  relatedId?: string
}

// Sistema de Permisos Granular
export interface ModulePermissions {
  dashboard: PermissionLevel
  patients: PermissionLevel
  doctors: PermissionLevel
  appointments: PermissionLevel
  clinicalHistory: PermissionLevel
  services: PermissionLevel
  insurance: PermissionLevel
  specialties: PermissionLevel
  contacts: PermissionLevel
  companies: PermissionLevel
  opportunities: PermissionLevel
  settings: PermissionLevel
}

export interface RoleConfig {
  name: string
  description: string
  permissions: ModulePermissions
}

// Configuración de roles predeterminados
export const DEFAULT_ROLE_CONFIGS: Record<UserRole, RoleConfig> = {
  administrador: {
    name: 'Administrador',
    description: 'Acceso total al sistema',
    permissions: {
      dashboard: 'full',
      patients: 'full',
      doctors: 'full',
      appointments: 'full',
      clinicalHistory: 'full',
      services: 'full',
      insurance: 'full',
      specialties: 'full',
      contacts: 'full',
      companies: 'full',
      opportunities: 'full',
      settings: 'full',
    },
  },
  doctor: {
    name: 'Doctor',
    description: 'Acceso a pacientes y citas propias',
    permissions: {
      dashboard: 'own',
      patients: 'read',
      doctors: 'read',
      appointments: 'own',
      clinicalHistory: 'full',
      services: 'read',
      insurance: 'none',
      specialties: 'read',
      contacts: 'none',
      companies: 'none',
      opportunities: 'none',
      settings: 'none',
    },
  },
  recepcionista: {
    name: 'Recepcionista',
    description: 'Gestión de citas y pacientes del día',
    permissions: {
      dashboard: 'read',
      patients: 'full',
      doctors: 'read',
      appointments: 'full',
      clinicalHistory: 'none',
      services: 'read',
      insurance: 'full',
      specialties: 'read',
      contacts: 'full',
      companies: 'read',
      opportunities: 'none',
      settings: 'none',
    },
  },
  callcenter: {
    name: 'Call Center',
    description: 'Gestión de leads y oportunidades',
    permissions: {
      dashboard: 'read',
      patients: 'read',
      doctors: 'read',
      appointments: 'read',
      clinicalHistory: 'none',
      services: 'read',
      insurance: 'none',
      specialties: 'read',
      contacts: 'full',
      companies: 'full',
      opportunities: 'full',
      settings: 'none',
    },
  },
}

// Helper para obtener permisos de un rol (con soporte para permisos personalizados)
export function getRolePermissions(role: UserRole): ModulePermissions {
  // Try to load custom permissions from localStorage
  try {
    const savedPermissions = localStorage.getItem('savidna-role-permissions')
    if (savedPermissions) {
      const customPermissions = JSON.parse(savedPermissions)

      // Build ModulePermissions object from custom permissions matrix
      const modulePermissions: ModulePermissions = {
        dashboard:
          customPermissions.dashboard?.[role] ||
          DEFAULT_ROLE_CONFIGS[role].permissions.dashboard,
        patients:
          customPermissions.patients?.[role] ||
          DEFAULT_ROLE_CONFIGS[role].permissions.patients,
        doctors:
          customPermissions.doctors?.[role] ||
          DEFAULT_ROLE_CONFIGS[role].permissions.doctors,
        appointments:
          customPermissions.appointments?.[role] ||
          DEFAULT_ROLE_CONFIGS[role].permissions.appointments,
        clinicalHistory:
          customPermissions.clinicalHistory?.[role] ||
          DEFAULT_ROLE_CONFIGS[role].permissions.clinicalHistory,
        services:
          customPermissions.services?.[role] ||
          DEFAULT_ROLE_CONFIGS[role].permissions.services,
        insurance:
          customPermissions.insurance?.[role] ||
          DEFAULT_ROLE_CONFIGS[role].permissions.insurance,
        specialties:
          customPermissions.specialties?.[role] ||
          DEFAULT_ROLE_CONFIGS[role].permissions.specialties,
        contacts:
          customPermissions.contacts?.[role] ||
          DEFAULT_ROLE_CONFIGS[role].permissions.contacts,
        companies:
          customPermissions.companies?.[role] ||
          DEFAULT_ROLE_CONFIGS[role].permissions.companies,
        opportunities:
          customPermissions.opportunities?.[role] ||
          DEFAULT_ROLE_CONFIGS[role].permissions.opportunities,
        settings:
          customPermissions.settings?.[role] ||
          DEFAULT_ROLE_CONFIGS[role].permissions.settings,
      }

      return modulePermissions
    }
  } catch (e) {
    console.error('Error loading custom permissions:', e)
  }

  // Fall back to default permissions
  return DEFAULT_ROLE_CONFIGS[role].permissions
}

// Helper para verificar si un usuario tiene permiso
export function hasPermission(
  userRole: UserRole,
  module: keyof ModulePermissions,
  requiredLevel: PermissionLevel = 'read',
): boolean {
  const permissions = getRolePermissions(userRole)
  const userLevel = permissions[module]

  if (userLevel === 'none') return false
  if (userLevel === 'full') return true
  if (requiredLevel === 'read' && (userLevel === 'read' || userLevel === 'own'))
    return true
  if (requiredLevel === 'own' && userLevel === 'own') return true

  return false
}
